import './priceinfo.scss';

import React, { useContext, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import CONFIG from '../../../common/config';
import { KiaContext } from '../../../common/context/main.context';
import GTM from '../../../common/services/gtm.service';
import { formatPrice } from '../../../common/utils/price.utils';
import Button from '../../01_atoms/button/Button';
import Icon from '../../01_atoms/icons/Icon';
import Popup from '../popup/Popup';

export default function PriceInfo(props) {
  const {
    title,
    subtitle,
    className = '',
    price,
    description,
    actions,
    button,
    buttonLabel = '',
    promo = '',
    model = '',
    isB2c,
    legal,
    popupClassName = '',
  } = props;

  const info = useRef();
  const { currentLocale, bms, bmsExtended } = useContext(KiaContext);

  const [open, setOpen] = useState(false);

  const onTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);

    GTM.track({
      dlCategory: 'Price details',
      dlAction: 'Click',
      dlLabel: buttonLabel ? title : 'i',
      model,
    });
  };

  const onDocumentClick = (e) => {
    const isPopupChild = e.target.closest('.popup');

    if (!isPopupChild) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    const timer = window.setTimeout(() => {
      window.document.addEventListener('click', onDocumentClick);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      window.document.removeEventListener('click', onDocumentClick);
    };
  }, [open]);

  // useEffect(() => {
  //   if (!info || !info.current) { return; }
  //   const $actions = info.current.querySelectorAll('.button');

  //   [...$actions].forEach($action => $action.addEventListener('click', onActionClick));

  //   return () => {
  //     [...$actions].forEach($action => $action.removeEventListener('click', onActionClick));
  //   }
  // }, [info]);

  const hasPriceTakeover = price.takeover && price.takeover !== '0.00'
  const hasPriceRecycleBonus = price.recycle_bonus && price.recycle_bonus !== '0.00'
  const hasPriceDiscount = price.discount && price.discount !== '0.00'
  const hasPriceBonus = price.bonus && price.bonus !== '0.00'
  const hasPriceExtraAdvantage = price.extra_advantage && price.extra_advantage !== '0.00'

  // Case 1: Both green values = 0 → Purple = Red, only show Purple
  const onlyShowCatalogPrice = price.catalog && !hasPriceDiscount && !hasPriceTakeover && !hasPriceRecycleBonus

  // Determine translation key for netto price (Purple) based on 4 cases
  const getNettoPriceKey = () => {
    if (!hasPriceTakeover && !hasPriceRecycleBonus) {
      // FR: "Prix net" / NL: "Nettoprijs"
      return 'priceinfo.nettoprice.short'
    } else if (hasPriceTakeover && !hasPriceRecycleBonus) {
      // FR: "Prix net (prime de reprise exclue)" / NL: "Nettoprijs (excl. overnamepremie)"
      return 'priceinfo.nettoprice'
    } else if (!hasPriceTakeover && hasPriceRecycleBonus) {
      // FR: "Prix net (prime de recyclage exclue)" / NL: "Nettoprijs (excl. recyclagepremie) (3)"
      return 'priceinfo.nettoprice.recycleonly'
    } else {
      // FR: "Prix net (prime de reprise et de recyclage exclue)" / NL: "Nettoprijs (excl. overnamepremie en recyclagepremie) (3)"
      return 'priceinfo.nettoprice.both'
    }
  }

  // Determine translation key for salon price (Red) based on 4 cases
  const getSalonPriceKey = () => {
    if (!hasPriceTakeover && !hasPriceRecycleBonus) {
      return 'priceinfo.salonprice.short'
    } else if (hasPriceTakeover && !hasPriceRecycleBonus) {
      // FR: "Prix promo (prime de reprise inclue)" / NL: "Promoprijs (incl. overnamepremie)"
      if (bmsExtended) {
        return 'priceinfo.salonprice.bms.extended'
      } else if (bms) {
        return 'priceinfo.salonprice.bms'
      }
      return 'priceinfo.salonprice'
    } else if (!hasPriceTakeover && hasPriceRecycleBonus) {
      // FR: "Prix promo (prime de recyclage inclue) (3)" / NL: "Promoprijs (incl. recyclagepremie) (3)"
      if (bmsExtended) {
        return 'priceinfo.salonprice.bms.extended.recycleonly'
      } else if (bms) {
        return 'priceinfo.salonprice.bms.recycleonly'
      }
      return 'priceinfo.salonprice.recycleonly'
    } else {
      // FR: "Prix promo (prime de reprise et de recyclage inclue) (3)" / NL: "Promoprijs (incl. overnamepremie en recyclagepremie) (3)"
      if (bmsExtended) {
        return 'priceinfo.salonprice.bms.extended.both'
      } else if (bms) {
        return 'priceinfo.salonprice.bms.both'
      }
      return 'priceinfo.salonprice.both'
    }
  }

  // Determine translation key for discount based on BMS status
  const getDiscountMessageId = () => {
    if (bmsExtended) {
      return "priceinfo.discountbms.extended";
    } else if (bms) {
      return "priceinfo.discountbms";
    } else {
      return "priceinfo.discount";
    }
  }

  const isShowLegal = isB2c && legal && title.includes('EV4 Fastback');

  return (
    <div className={`priceinfo ${className}`} ref={info}>
      {button && buttonLabel ? (
        <Button mode="naked" size="small" underline onClick={onTriggerClick}>{buttonLabel}</Button>
      ) : (
        <Icon icon="alert" onClick={onTriggerClick} />
      )}

      {(price || description || actions) ? (
        <Popup name="priceinfo" open={open} className={popupClassName}>
          <p className="p--1 bold mt--0 mb--1">{title}</p>
          {subtitle && <p className="priceinfo__subtitle my--0">{subtitle}</p>}
          {promo ? (<div className="priceinfo__promo" dangerouslySetInnerHTML={{ __html: promo }} />) : null}
          {isB2c && <table className="priceinfo__table">
            <tbody>
              {price.catalog ? (
                <tr>
                  <td><FormattedMessage id="priceinfo.catalogprice" /></td>
                  <td className="bold priceinfo__price">{formatPrice(price.catalog, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
              ) : null}
              {!onlyShowCatalogPrice && hasPriceDiscount ? (
                <tr>
                  <td><FormattedMessage id={getDiscountMessageId()} /></td>
                  <td className="bold priceinfo__price">-{formatPrice(price.discount, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
              ) : null}
              {!onlyShowCatalogPrice && hasPriceExtraAdvantage ?
                <tr>
                  <td>{price.extra_advantage_label}</td>
                  <td className="bold priceinfo__price">-{formatPrice(price.extra_advantage, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
                : null}
              {hasPriceBonus ? (
                <tr>
                  <td><FormattedMessage id="priceinfo.bonus" /></td>
                  <td className="bold priceinfo__price">-{formatPrice(price.bonus, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
              ) : null}
              {!onlyShowCatalogPrice && price.netto ? (
                <tr>
                  <td><FormattedMessage id={getNettoPriceKey()} /></td>
                  <td className="bold priceinfo__price">{formatPrice(price.netto, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
              ) : null}
              {!onlyShowCatalogPrice && hasPriceTakeover ? (
                <tr>
                  {(model.includes('picanto') && false) ? (<td><FormattedMessage id="priceinfo.recycleprice" /></td>) : (<td><FormattedMessage id="priceinfo.takeoverprice" /></td>)}

                  <td className="bold priceinfo__price">-{formatPrice(price.takeover, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
              ) : null}
              {!onlyShowCatalogPrice && hasPriceRecycleBonus ? (
                <tr>
                  <td><FormattedMessage id="priceinfo.recycleprice" /></td>
                  <td className="bold priceinfo__price">-{formatPrice(price.recycle_bonus, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
              ) : null}
              {!onlyShowCatalogPrice && price.salon && (hasPriceTakeover || hasPriceRecycleBonus) ? (
                <tr>
                  <td><FormattedMessage id={getSalonPriceKey()} /></td>
                  <td className="bold priceinfo__price">{formatPrice(price.salon, CONFIG.PRICE_FORMAT, currentLocale.locale)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>}

          {props.children}

          {description ? (<div className="my--3 p--2" dangerouslySetInnerHTML={{ __html: description }} />) : null}

          {actions ? (<div className="priceinfo__actions">{actions}</div>) : null}

          {isShowLegal ? (<div className="mt--3 priceinfo__legal" dangerouslySetInnerHTML={{ __html: legal }} />) : null}
        </Popup>
      ) : null}
    </div>
  );
}
